import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import Button from "components/form/Button";
import {
  formatCurrency,
  formatFrequency,
  sortIntervals,
} from "utils/HelperFunctions";
import { PlanFrequency, PlanSelectionProps } from "types/subscriptionAndPlans";
import PlanCard from "./PlanCard";
import CurrentPlanCard from "./CurrentPlanCard";

const PlanSelection: React.FC<PlanSelectionProps> = ({
  isLoading,
  selectedPlan,
  planOptions,
  products,
  currentProducts,
  totalAmount,
  isFetching,
  dismissedCurrentPids,
  activeSubtotalFrequency,
  missingCurrentItems,
  missingCurrentTotalForSubtotal,
  onChangePlanTab,
  onSelectPlan,
  onGetDueAmount,
  onUpdateQuantity,
  onToggleMissingCurrent,
  isSameCustomAndFreqInterval,
  planFrequency,
  missingPlanFrequency,
}) => {
  const sortedFrequency = useMemo(() => {
    if (!planFrequency && !missingPlanFrequency) return [];
    return sortIntervals([...planFrequency, ...missingPlanFrequency]);
  }, [planFrequency, missingPlanFrequency]);

  return (
    <>
      <div className="pt-5 w-full flex flex-col lg:gap-10 gap-5 relative lg:h-[65%] h-[73%] overflow-y-scroll lg:px-10 px-5">
        <p className="text-base lg:text-xl font-medium text-txtPrimary">
          Select Plan
        </p>
        <div className="flex lg:gap-3 gap-2 flex-wrap transition-all ease-in-out duration-500 ">
          {isLoading &&
            [1, 2, 3].map((item) => (
              <Skeleton key={item} width={100} height={46} />
            ))}
          {planOptions &&
            !isLoading &&
            sortedFrequency &&
            sortedFrequency.map((data: PlanFrequency, index: number) => {
              const item = {
                name: data.name,
                value: data.value,
                interval_count: data.interval_custom_count || 1,
                interval_selector: data.interval_custom_selector || "month",
                bottomDisplay: data.bottomDisplay || "",
              };
              return (
                <div
                  key={index}
                  className={`lg:px-5 px-4 lg:py-3 py-2 flex items-center gap-2 animate__animated animate__fadeIn border-[1px] rounded-md cursor-pointer ${
                    selectedPlan?.value === item?.value
                      ? "text-btnPrimaryActive border-btnPrimaryActive bg-white"
                      : "bg-white text-txtSecondary border-strokePrimary hover:!border-strokeActive"
                  }`}
                  onClick={() => {
                    onChangePlanTab(item);
                  }}
                >
                  <p className="text-sm font-normal capitalize">{item.name}</p>
                </div>
              );
            })}
        </div>
        <div className="w-full flex flex-col lg:gap-5 gap-3">
          {isLoading ? (
            <>
              {[1, 2, 3, 4, 5].map((item, index) => (
                <div
                  className="w-full lg:p-5 p-4 rounded-md border animate__animated animate__fadeIn border-strokePrimary"
                  key={index}
                >
                  <div className="flex flex-col justify-center">
                    <div className=" flex gap-2 items-center ">
                      <Skeleton width={30} height={30} />
                      <Skeleton width={150} height={25} />
                    </div>
                    <div className="lg:mt-2 mt-[6px]">
                      <Skeleton width={250} height={20} />
                    </div>
                    <div className="lg:mt-4 mt-3">
                      <Skeleton width={350} height={20} />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            planOptions[selectedPlan.value] &&
            planOptions[selectedPlan.value]?.map((item: any, index: number) => {
              let quantity =
                products.filter((obj: any) => obj.pid === item?.id)[0]
                  ?.quantity || 1;
              return (
                <PlanCard
                  key={item?.id}
                  index={item?.id}
                  item={item}
                  isSelected={products.some((obj: any) => obj.pid === item?.id)}
                  quantity={quantity}
                  isCurrentPlan={currentProducts.some(
                    (obj: any) => obj.pid === item?.id,
                  )}
                  allowQuantityChange={true}
                  onSelectPlan={onSelectPlan}
                  onUpdateQuantity={onUpdateQuantity}
                />
              );
            })
          )}
          {!isLoading &&
            missingCurrentItems
              ?.filter((cp) => cp.schedule_display === selectedPlan?.value)
              ?.map((cp: any, idx: number) => (
                <CurrentPlanCard
                  key={`current-${idx}`}
                  index={`current-${idx}`}
                  item={cp}
                  selectedPlan={selectedPlan}
                  isDismissed={dismissedCurrentPids.includes(
                    String(cp?.pid ?? ""),
                  )}
                  onToggle={onToggleMissingCurrent}
                />
              ))}
        </div>
      </div>
      {!isSameCustomAndFreqInterval && (
        <p className="text-sm text-red text-left lg:px-10 px-5 mt-1">
          Please select a product with the same billing period.
        </p>
      )}
      <div className="lg:px-10 lg:py-0 p-5 w-full lg:mt-9 lg:pb-2">
        <div className="w-full flex justify-between lg:text-xl text-lg font-bold text-txtPrimary ">
          <div>Subtotal</div>
          {isLoading ? (
            <Skeleton width={100} height={20} />
          ) : (
            <div>
              {formatCurrency(
                (totalAmount || 0) + (missingCurrentTotalForSubtotal || 0),
              ) || "$0.00"}{" "}
              <span className="lowercase">
                {activeSubtotalFrequency.bottomDisplay}
              </span>
            </div>
          )}
        </div>
        <div className="w-full grid place-items-end mt-[30px]">
          <Button
            isDisabled={
              isLoading ||
              ((products?.length || 0) === 0 &&
                (missingCurrentTotalForSubtotal || 0) === 0) ||
              !isSameCustomAndFreqInterval
            }
            text="Continue"
            onClick={() => onGetDueAmount()}
            className="lg:!px-10 !px-[25px]"
            isLoading={isFetching}
          />
        </div>
      </div>
    </>
  );
};

export default PlanSelection;
